// JOBE-0158.7: drillContext URL-fragment helpers.
//
// Wire J1's "drill down then return to W1 with the originating attention
// row updated" loop (ADR-0034 §D4). When a user drills from one surface
// to another, the originating filter / view state travels in the URL
// fragment so:
//
//   - Browser back/forward replay the originating view exactly.
//   - A "Return to {origin}" action on the drill-down surface can hop
//     back to the precise state we came from.
//   - Sharing the URL preserves the drill context for the recipient.
//
// Fragments (not query strings) carry this transport because they don't
// trigger server round-trips, browsers replay them faithfully across
// back/forward, and query params on the destination already carry
// destination-meaningful state.
//
// Encoding: `#from=<base64(encodeURIComponent(JSON.stringify(context)))>`.
// encodeURIComponent runs first to handle non-ASCII safely; btoa then
// produces a URL-safe ASCII payload.
//
// Size cap: 2 KB encoded. Larger payloads drop with a console.warn —
// store the heavy state in a Zustand slice keyed by id and pass the id.
//
// Decoding never throws; malformed input returns null so a hand-edited
// URL can't crash the page.
//
// ── Surface integration pattern ──────────────────────────────────────
//
// Originating surface (e.g. W1 attention row):
//
//   import { withDrillContext } from '../lib/drillContext.js';
//
//   const onAttentionRowClick = (row) => {
//     const context = { from: 'w1', filters: currentFilters, focus: row.id };
//     navigate(withDrillContext(`/w4/${row.dept}`, context));
//   };
//
// Drill-down surface (e.g. W4 department view):
//
//   import { useDrillContext, clearDrillContext } from '../lib/drillContext.js';
//
//   function W4() {
//     const context = useDrillContext();
//     // context is { from: 'w1', filters: {…}, focus: '…' } or null
//
//     if (context?.from === 'w1') {
//       return (
//         <button
//           onClick={() =>
//             clearDrillContext((ctx) => navigate(`/?focus=${ctx.focus}`))
//           }
//         >
//           ← Back to Command Center
//         </button>
//       );
//     }
//   }

import { useLocation } from 'react-router-dom';

// Fragment key kept as a single source of truth so consumers can grep.
const FRAGMENT_KEY = 'from';
const MAX_ENCODED_BYTES = 2048;

// ── internal helpers ─────────────────────────────────────────────────

// Encode a JSON-serializable value to a URL-safe ASCII string.
// encodeURIComponent first so multi-byte chars survive btoa's
// Latin-1-only restriction.
function encodeContext(context) {
  return btoa(encodeURIComponent(JSON.stringify(context)));
}

// Inverse of encodeContext. Returns null on any failure (invalid base64,
// invalid percent-encoding, invalid JSON, anything else).
function decodeContext(encoded) {
  try {
    const decoded = decodeURIComponent(atob(encoded));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

// Split a hash string ("#a=1&b=2" or "a=1&b=2") into [{key, value}, …].
// Tolerates a leading '#' or its absence.
function parseHashEntries(hash) {
  if (!hash) return [];
  const body = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!body) return [];
  return body.split('&').map((seg) => {
    const eq = seg.indexOf('=');
    if (eq === -1) return { key: seg, value: '' };
    return { key: seg.slice(0, eq), value: seg.slice(eq + 1) };
  });
}

// Inverse: rebuild the hash body (without the leading '#') from entries.
function serializeHashEntries(entries) {
  return entries
    .map((e) => (e.value === '' && !e.key.includes('=') ? e.key : `${e.key}=${e.value}`))
    .join('&');
}

// Read the drill context out of a hash string. Pure — does not touch
// window. Returns the parsed object, or null if missing/malformed.
function parseFromFragment(hash) {
  const entries = parseHashEntries(hash);
  const entry = entries.find((e) => e.key === FRAGMENT_KEY);
  if (!entry) return null;
  return decodeContext(entry.value);
}

// Split a URL into [pathAndQuery, hash] so we can rewrite the fragment
// without disturbing the path or query string. Works on absolute and
// relative URLs alike because we treat the input as opaque.
function splitHash(url) {
  const i = url.indexOf('#');
  if (i === -1) return [url, ''];
  return [url.slice(0, i), url.slice(i + 1)];
}

// ── public API ───────────────────────────────────────────────────────

/**
 * Append a drill-context fragment to a target URL.
 *
 * - If the URL already has `#from=…`, replace that segment in place.
 * - If the URL has a non-`from` fragment (e.g. `#tab=okrs`), append
 *   `&from=…` to it.
 * - If the URL has no fragment, append `#from=…`.
 *
 * If the encoded fragment exceeds {@link MAX_ENCODED_BYTES} (2 KB), the
 * context is dropped and the original `targetUrl` is returned with a
 * `console.warn`. Heavy payloads should be stored out-of-band and
 * referenced by id.
 *
 * @param {string} targetUrl Destination route, with or without
 *   existing query/fragment.
 * @param {object} context JSON-serializable plain object.
 * @returns {string} URL with the drill context attached, or the
 *   original URL unchanged when the payload is too large.
 */
export function withDrillContext(targetUrl, context) {
  const encoded = encodeContext(context);
  if (encoded.length > MAX_ENCODED_BYTES) {
    // eslint-disable-next-line no-console
    console.warn(
      `[drillContext] Encoded payload ${encoded.length}B exceeds ${MAX_ENCODED_BYTES}B cap; dropping. ` +
        'Store the state in a Zustand slice and pass an id in the fragment instead.',
    );
    return targetUrl;
  }

  const [base, hash] = splitHash(targetUrl);
  const entries = parseHashEntries(hash);
  const existingIdx = entries.findIndex((e) => e.key === FRAGMENT_KEY);
  if (existingIdx === -1) {
    entries.push({ key: FRAGMENT_KEY, value: encoded });
  } else {
    entries[existingIdx] = { key: FRAGMENT_KEY, value: encoded };
  }
  return `${base}#${serializeHashEntries(entries)}`;
}

/**
 * React hook returning the parsed drill context from the current URL
 * fragment, or `null` if no `from=` fragment is present (or it's
 * malformed).
 *
 * Re-fires on navigation by subscribing to react-router's `useLocation`.
 * Parses on every render — drill context is small and parsing is cheap,
 * which avoids cache staleness across back/forward.
 *
 * @returns {object | null}
 */
export function useDrillContext() {
  const { hash } = useLocation();
  return parseFromFragment(hash);
}

/**
 * Navigation helper for return-from-drill actions.
 *
 * Reads the current context from `window.location.hash`, calls
 * `onReturn(context)` if supplied (even with `null` when no context
 * exists), then strips the `from=` segment from the current URL via
 * `history.replaceState` so the cleared URL doesn't create a
 * back-button entry. When no `from=` segment is present, the URL is
 * left untouched (no replaceState call).
 *
 * @param {(context: object | null) => void} [onReturn] Callback fired
 *   with the parsed context (or null) before the URL is rewritten. The
 *   callback typically navigates back to the originating surface.
 */
export function clearDrillContext(onReturn) {
  const context = parseFromFragment(window.location.hash);
  if (typeof onReturn === 'function') {
    onReturn(context);
  }
  if (context === null) {
    // Nothing to clear; don't churn history.
    return;
  }
  const entries = parseHashEntries(window.location.hash).filter(
    (e) => e.key !== FRAGMENT_KEY,
  );
  const newHash = entries.length ? `#${serializeHashEntries(entries)}` : '';
  const { pathname, search } = window.location;
  window.history.replaceState({}, '', `${pathname}${search}${newHash}`);
}
